import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { MessageSquare, Trash2, Loader2, Users } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import {
  listCommunityNotes,
  createCommunityNote,
  deleteCommunityNote,
} from '@/lib/api';
import { toast } from 'sonner';

const NOTE_MAX = 500;

function formatRelative(iso) {
  if (!iso) return '';
  const then = new Date(iso);
  const diffMs = Date.now() - then.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  return then.toLocaleDateString();
}

export default function CommunityResearchNotes({ caseId }) {
  const { user, isAuthenticated } = useAuth();
  const isAdmin = !!user?.is_admin;

  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const refresh = useCallback(async () => {
    if (!caseId) return;
    setLoading(true);
    try {
      const data = await listCommunityNotes(caseId);
      setNotes(data.notes || []);
    } catch (err) {
      // 404/empty fine
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const submit = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    if (trimmed.length > NOTE_MAX) {
      toast.error(`Notes are limited to ${NOTE_MAX} characters.`);
      return;
    }
    setSubmitting(true);
    try {
      await createCommunityNote(caseId, trimmed);
      setText('');
      await refresh();
      toast.success('Research note added.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not add note.');
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (noteId) => {
    if (!window.confirm('Delete this community note?')) return;
    try {
      await deleteCommunityNote(noteId);
      await refresh();
      toast.success('Note deleted.');
    } catch (err) {
      toast.error('Could not delete note.');
    }
  };

  const remaining = NOTE_MAX - text.length;

  return (
    <Card data-testid="community-research-notes" className="border-indigo-200/70 dark:border-indigo-900/50">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading flex items-center gap-2 text-base sm:text-lg">
          <Users className="w-5 h-5 text-indigo-500" />
          Community Research
          {!loading && notes.length > 0 && (
            <Badge variant="secondary" className="ml-1 text-xs">
              {notes.length}
            </Badge>
          )}
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Open notes from anyone investigating this case. Be respectful, cite sources.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Composer */}
        {isAuthenticated ? (
          <div className="space-y-2" data-testid="community-research-composer">
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value.slice(0, NOTE_MAX))}
              placeholder="Share what you're seeing — a possible lead, a contradiction in the record, a database to check next…"
              maxLength={NOTE_MAX}
              rows={3}
              className="text-sm"
              data-testid="community-note-input"
            />
            <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-2">
              <span className={`text-xs ${remaining < 50 ? 'text-amber-600' : 'text-muted-foreground'}`}>
                {remaining} characters left · plain text · public
              </span>
              <Button
                size="sm"
                onClick={submit}
                disabled={submitting || !text.trim()}
                data-testid="community-note-submit"
                className="w-full sm:w-auto"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <MessageSquare className="w-4 h-4 mr-2" />
                )}
                Post note
              </Button>
            </div>
          </div>
        ) : (
          <div className="rounded-md border bg-slate-50 dark:bg-slate-900/40 px-3 py-3 text-sm text-muted-foreground">
            <Link to="/login" className="text-indigo-600 underline">
              Sign in
            </Link>{' '}
            (free) to post research notes on this case.
          </div>
        )}

        {/* Notes list */}
        <div className="space-y-3" data-testid="community-notes-list">
          {loading ? (
            <div className="text-xs text-muted-foreground flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" /> loading notes…
            </div>
          ) : notes.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">
              No community notes yet. Be the first to share a lead.
            </p>
          ) : (
            notes.map((n) => (
              <div
                key={n.note_id}
                data-testid={`community-note-${n.note_id}`}
                className="rounded-md border bg-white dark:bg-slate-900/40 p-3"
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="text-xs">
                    <span className="font-semibold text-foreground">{n.user_name}</span>
                    <span className="text-muted-foreground"> · {formatRelative(n.created_at)}</span>
                  </div>
                  {isAdmin && (
                    <button
                      onClick={() => remove(n.note_id)}
                      className="text-muted-foreground hover:text-red-600 transition-colors"
                      data-testid={`delete-note-${n.note_id}`}
                      title="Delete (admin)"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                <p className="text-sm whitespace-pre-wrap break-words text-foreground/90">
                  {n.text}
                </p>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
}
